// Singleton BunkerSession wrapper — NIP-46 signing via Keycast.
// connect(), reconnect(), signEvent(), disconnect(), isConnected().
// Device-scoped credentials (bunker URL + client key) survive logout.
// Dedup guards prevent React 18 StrictMode double-mount issues.

import { BunkerSession } from '@synvya/auth-identity'
import type { BunkerSessionOptions } from '@synvya/auth-identity'
import type { EventTemplate, VerifiedEvent } from 'nostr-tools'

let session: BunkerSession | null = null
let pendingReconnect: Promise<string | null> | null = null
const pendingConnects = new Map<string, Promise<string>>()

function sessionOptions(): BunkerSessionOptions {
  return {}
}

export async function connect(bunkerUrl: string): Promise<string> {
  const existing = pendingConnects.get(bunkerUrl)
  if (existing) return existing

  const promise = (async () => {
    if (session && !session.isClosed()) {
      const currentUrl = session.getBunkerUrl()
      if (currentUrl === bunkerUrl) return session.getPublicKey()
      await session.disconnect()
    }
    session = await BunkerSession.connect(bunkerUrl, sessionOptions())
    return session.getPublicKey()
  })()

  pendingConnects.set(bunkerUrl, promise)
  try {
    return await promise
  } finally {
    pendingConnects.delete(bunkerUrl)
  }
}

export async function reconnect(): Promise<string | null> {
  if (session && !session.isClosed()) return session.getPublicKey()
  if (pendingReconnect) return pendingReconnect

  pendingReconnect = (async () => {
    try {
      session = await BunkerSession.tryReconnect(sessionOptions())
      return session ? session.getPublicKey() : null
    } catch {
      return null
    }
  })()

  try {
    return await pendingReconnect
  } finally {
    pendingReconnect = null
  }
}

export async function signEvent(
  template: EventTemplate
): Promise<VerifiedEvent> {
  if (!session || session.isClosed()) {
    throw new Error('Bunker not connected — call connect() first')
  }
  return session.signEvent(template)
}

export async function nip44Encrypt(
  plaintext: string,
  peerPubkey: string
): Promise<string> {
  if (!session || session.isClosed()) {
    throw new Error('Bunker not connected — call connect() first')
  }
  return session.nip44Encrypt(peerPubkey, plaintext)
}

export async function nip44Decrypt(
  ciphertext: string,
  peerPubkey: string
): Promise<string> {
  if (!session || session.isClosed()) {
    throw new Error('Bunker not connected — call connect() first')
  }
  return session.nip44Decrypt(peerPubkey, ciphertext)
}

export async function disconnect(): Promise<void> {
  if (session && !session.isClosed()) {
    await session.disconnect()
  }
  session = null
}

export function isConnected(): boolean {
  return session !== null && !session.isClosed()
}

export function getStoredBunkerUrl(): string | null {
  try {
    return localStorage.getItem('synvya_bunker_url')
  } catch {
    return null
  }
}

export function forgetStoredBunkerUrl(): void {
  try {
    localStorage.removeItem('synvya_bunker_url')
    localStorage.removeItem('synvya_nip46_client_key')
  } catch {
    // localStorage unavailable
  }
}
